'use client'

import { useCallback } from 'react'
import { useBirhaus } from '@birhaus/provider'
import { 
  formatCurrency, 
  parseCurrency, 
  convertCurrency, 
  getCurrencyInfo,
  validateCurrencyAmount,
  checkSEPRELADThreshold
} from '../utils/currency'
import type { 
  CurrencyCode, 
  CurrencyFormatOptions, 
  UseCurrencyReturn 
} from '../types/financial'

/**
 * useCurrency - Hook for currency operations and formatting
 * 
 * Features:
 * - Paraguay-first currency formatting with Guaraní support
 * - SEPRELAD compliance checking and reporting
 * - Multi-currency conversion with exchange rates
 * - Currency parsing and validation
 * - Spanish-first localization
 * - Error handling and validation
 * - Real-time exchange rate simulation (mock)
 */
export function useCurrency(): UseCurrencyReturn {
  const { language } = useBirhaus()

  // Format currency with BIRHAUS locale preferences
  const formatCurrencyAmount = useCallback((
    amount: number, 
    options?: Partial<CurrencyFormatOptions>
  ): string => {
    const locale = language === 'es' ? 'es-PY' : 'en-US'
    
    try {
      return formatCurrency(amount, {
        locale,
        ...options
      })
    } catch (error) {
      console.error('Currency formatting error:', error)
      // Fallback to simple number formatting
      return amount.toString()
    }
  }, [language])

  // Parse currency string to number
  const parseCurrencyString = useCallback((value: string): number | null => {
    try {
      return parseCurrency(value)
    } catch (error) {
      console.error('Currency parsing error:', error)
      return null
    }
  }, [])

  // Convert between currencies (mock implementation - in production, use real API)
  const convertCurrencyAmount = useCallback(async (
    amount: number, 
    from: CurrencyCode, 
    to: CurrencyCode
  ): Promise<number> => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 100))
    
    try {
      return convertCurrency(amount, from, to)
    } catch (error) {
      console.error('Currency conversion error:', error)
      throw error
    }
  }, [])

  // Get currency information
  const getCurrencyInformation = useCallback((code: CurrencyCode) => {
    return getCurrencyInfo(code)
  }, [])

  // Validate currency amount for processing
  const validateAmount = useCallback((
    amount: number, 
    currency: CurrencyCode = 'PYG'
  ): { valid: boolean; errors: string[]; warnings: string[] } => {
    const validation = validateCurrencyAmount(amount, currency)
    const warnings: string[] = []

    // Check SEPRELAD thresholds
    const seprelad = checkSEPRELADThreshold(amount, currency)
    if (seprelad.requiresReport) {
      warnings.push(
        language === 'es' 
          ? `Monto supera umbral SEPRELAD (${seprelad.thresholdUSD} USD)`
          : `Amount exceeds SEPRELAD threshold (${seprelad.thresholdUSD} USD)`
      )
    }

    return {
      ...validation,
      warnings
    }
  }, [language])

  // Format for input fields (no symbols, proper formatting)
  const formatForInput = useCallback((
    amount: number, 
    currency: CurrencyCode = 'PYG'
  ): string => {
    const currencyInfo = getCurrencyInfo(currency)
    
    if (currency === 'PYG') {
      // Guaraní: no decimals, dot as thousand separator
      return amount.toLocaleString('es-PY', {
        useGrouping: true,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })
    } else {
      // Other currencies: include decimals
      return amount.toLocaleString('es-PY', {
        useGrouping: true,
        minimumFractionDigits: currencyInfo.decimals,
        maximumFractionDigits: currencyInfo.decimals
      })
    }
  }, [])

  // Get exchange rate between currencies
  const getExchangeRate = useCallback(async (
    from: CurrencyCode, 
    to: CurrencyCode
  ): Promise<number> => {
    if (from === to) return 1
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 50))
    
    try {
      // Calculate rate by converting 1 unit
      return convertCurrency(1, from, to)
    } catch (error) {
      console.error('Exchange rate fetch error:', error)
      throw error
    }
  }, [])

  // Check if currency is supported
  const isCurrencySupported = useCallback((currency: string): currency is CurrencyCode => {
    return ['PYG', 'USD', 'EUR', 'BRL', 'ARS'].includes(currency as CurrencyCode)
  }, [])

  // Get localized currency name
  const getCurrencyName = useCallback((
    currency: CurrencyCode, 
    locale?: 'es' | 'en'
  ): string => {
    const currencyInfo = getCurrencyInfo(currency)
    const targetLocale = locale || language
    return targetLocale === 'es' ? currencyInfo.nameEs : currencyInfo.nameEn
  }, [language])

  // Format percentage for interest rates, commissions, etc.
  const formatPercentage = useCallback((
    value: number, 
    decimals: number = 2
  ): string => {
    const locale = language === 'es' ? 'es-PY' : 'en-US'
    return new Intl.NumberFormat(locale, {
      style: 'percent',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(value / 100)
  }, [language])

  // Calculate compound interest (useful for investment components)
  const calculateCompoundInterest = useCallback((
    principal: number,
    rate: number, // annual rate as percentage
    time: number, // time in years
    compounding: number = 12 // times per year
  ): { finalAmount: number; interestEarned: number } => {
    const r = rate / 100 / compounding
    const n = compounding * time
    const finalAmount = principal * Math.pow(1 + r, n)
    const interestEarned = finalAmount - principal
    
    return {
      finalAmount: Math.round(finalAmount * 100) / 100,
      interestEarned: Math.round(interestEarned * 100) / 100
    }
  }, [])

  // Format large numbers with abbreviations (K, M, B)
  const formatAbbreviated = useCallback((
    amount: number,
    currency: CurrencyCode = 'PYG'
  ): string => {
    const abs = Math.abs(amount)
    const sign = amount < 0 ? '-' : ''
    
    let value: number
    let suffix: string
    
    if (abs >= 1000000000) {
      value = amount / 1000000000
      suffix = language === 'es' ? 'MM' : 'B' // Mil millones vs Billions
    } else if (abs >= 1000000) {
      value = amount / 1000000
      suffix = 'M'
    } else if (abs >= 1000) {
      value = amount / 1000
      suffix = 'K'
    } else {
      return formatCurrencyAmount(amount, { currency })
    }
    
    const currencyInfo = getCurrencyInfo(currency)
    const rounded = Math.round(value * 10) / 10 // One decimal place
    
    return `${sign}${currencyInfo.symbol} ${rounded}${suffix}`
  }, [formatCurrencyAmount, language])

  return {
    formatCurrency: formatCurrencyAmount,
    parseCurrency: parseCurrencyString,
    convertCurrency: convertCurrencyAmount,
    getCurrencyInfo: getCurrencyInformation,
    validateAmount,
    formatForInput,
    getExchangeRate,
    isCurrencySupported,
    getCurrencyName,
    formatPercentage,
    calculateCompoundInterest,
    formatAbbreviated
  }
}

export type { UseCurrencyReturn }